import _                  from 'underscore';
import React, { FC }      from "react";
import { Formik }         from "formik";
import { Dropdown, Form } from "semantic-ui-react";
import * as Yup           from "yup";

import { ProjectQueries }                                    from "@src/queries/ProjectQueries";
import { UserQueries }                                       from "@src/queries";
import { useApolloClient, useQuery }                         from "@apollo/react-hooks";
import { QueryDialogProps, QueryOpDialog }                   from "@src/views/components/from-query/QueryOpDialog";
import { BasicDialogContainer }                              from "@src/views/components/modals/BasicDialogContainer";
import { Align, Size }                                       from "@src/types";
import { useCurrentUser, useFeatureFlags, useYupExtensions } from "@src/hooks";
import { runIfFunction }                                     from "@src/util";
import { TextAreaField, TextField }                          from "../../../components/forms";


interface Props extends QueryDialogProps {
    projectId: string;
    projectVersionId?: string;
    onVersionCreated?: ( version: any ) => void;
    onClose?: () => void;
}

interface FormValues {
    name: string;
    description: string;
    sourceProjectVersionId: string;
}

const useVersionOptions = ( projectId: string ) => {


    const { data: userData, loading: userLoading } = useQuery( UserQueries.queries.GET_CURRENT_USER(), {
        fetchPolicy: "network-only",
    } );

    const versionOptions = userLoading
                           ? []
                           : userData?.me?.projects
                                     .find( ( p ) => p.project.id === projectId )
                                     ?.project.projectVersions.map( ( version ) => ( {
            key:   version.id,
            text:  version.name,
            value: version.id,
        } ) ) || [];

    return { versionOptions, userLoading };
};

const CreateProjectVersionDialog: FC<Props> = ( props ) => {

    const { projectId, projectVersionId, onVersionCreated, onClose, ...queryDialogProps } = props;

    const { versionOptions, userLoading }    = useVersionOptions( projectId ),
          apolloClient                       = useApolloClient(),
          { currentUser }                    = useCurrentUser(),
          { addStringMinCharDescriptorTest } = useYupExtensions(),
          featureFlags                       = useFeatureFlags();


    const currentProject = currentUser.projects?.find( p => p.projectId === projectId );


    const initialValues: FormValues = {
        name:                   "",
        description:            "",
        sourceProjectVersionId: projectVersionId,
    };

    addStringMinCharDescriptorTest();

    const validationSchema = Yup.object().shape( {
        sourceProjectVersionId: Yup.string().required( "A source version is required" ),
        name:                   Yup.string().test( 'unique name', 'Version names must be unique', ( value: string ) => {
            let existingProjectVersionNames = [ ..._.map( currentProject.project?.projectVersions ?? [], item => item.name ) ];
            return !existingProjectVersionNames.includes( value?.trim() ?? '' );
        } ).required( "Version name is required" ),
        description:            Yup.string().hasDescriptorMinChar( {
            minChar:                     featureFlags.systemSettingFormFieldsDescriptorsMinCharacterLength,
            requiredWhenEnforcedMessage: "Version description is required"
        } ),
    } );

    return (
        <Formik<FormValues>
            initialValues={ initialValues }
            validateOnChange={ true }
            validationSchema={ validationSchema }
            onSubmit={ null }
        >
            { ( formik ) => (
                <QueryOpDialog
                    { ...queryDialogProps }
                    className={ 'no-padding' }
                    alignControls={ Align.CENTER }
                    operationDisabled={ !formik.isValid }
                    submitLabel="Create Version"
                    onCancel={ onClose }
                    onSubmit={ () => formik.submitForm() }
                    mutation={ ProjectQueries.mutations.CREATE_PROJECT_VERSION( ProjectQueries.fragments.PROJECT_VERSIONS.EDIT_FIELDS ) }
                    graphArgs={ {
                        variables:           {
                            input: {
                                projectId,
                                sourceProjectVersionId: formik.values.sourceProjectVersionId,
                                name:                   formik.values.name,
                                description:            formik.values.description,
                            },
                        },
                        awaitRefetchQueries: true,
                        refetchQueries:      [
                            { query: UserQueries.queries.GET_CURRENT_USER() }
                        ],
                        update:              async ( store: any, response: any ) => {
                            // Update current user with newly evaluated project list
                            let user = await apolloClient.query( { query: UserQueries.queries.GET_CURRENT_USER() } );
                            let updatedData = user.data.me;

                            store.writeQuery( {
                                query: UserQueries.queries.GET_CURRENT_USER(),
                                data:  { me: updatedData }
                            } );
                        }
                    } }
                    onComplete={ ( data ) => {

                        runIfFunction( onVersionCreated, data.createProjectVersion.id );

                        onClose();
                    } }
                >
                    <Form>
                        <BasicDialogContainer title={ 'Create New Project Version' } roundedTop contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] } onClose={ onClose }>
                            <Form.Field>
                                <label>Origin Version</label>
                                <Dropdown
                                    placeholder="Select Origin Version"
                                    fluid
                                    selection
                                    loading={ userLoading }
                                    disabled={ userLoading }
                                    options={ versionOptions }
                                    value={ formik.values.sourceProjectVersionId }
                                    onChange={ ( _, data ) => formik.setFieldValue( "sourceProjectVersionId", data.value ) }
                                />
                            </Form.Field>
                            <TextField name="name" label="Version Name" placeholder="Enter version name"/>
                            <TextAreaField name="description" label="Description" placeholder="Enter version description"/>
                        </BasicDialogContainer>
                    </Form>
                </QueryOpDialog>
            ) }
        </Formik>
    );
};

export { CreateProjectVersionDialog };
